/**
 * Subjects offered when uploading a patient document.
 *
 * The list lives in the FileSubjects table and is served by Files/GetFileSubjects — company
 * defaults plus anything that company added — so adding a subject needs no deploy. That follows
 * how the rest of the system handles pick-lists (TreatmentTypes, FundingSources, Medias …); the
 * hardcoded 1-5 urgency scale is the exception, and a fixed ordinal scale is a fair one.
 *
 * UploadedFiles.Subject stores the chosen name as text, not an id: the subject is interpolated
 * straight into the therapist SMS/email, and rows that predate the table are already free text.
 */

/**
 * Sentinel for "other". Never stored.
 *
 * In the upload form it reveals a free-text field; in the filter it selects every file whose
 * subject is not one of the configured ones. Same idea in both places, so one constant.
 */
export const OTHER_SUBJECT_VALUE = "__other__";

/** True when a file's subject is not one of the configured subjects — i.e. it belongs to "other". */
export function isOtherSubject(
  subject: string | null | undefined,
  configuredSubjects: string[],
): boolean {
  return !configuredSubjects.includes((subject ?? "").trim());
}

/**
 * Filter choices for a set of files: the configured subjects that actually have files here, plus a
 * single "other" bucket when anything falls outside the list.
 *
 * Free text is never promoted to its own filter row — a patient with ten custom subjects would
 * otherwise get ten one-file filters. Conversely a configured subject with no files here is
 * dropped, so no choice ever leads to an empty list. Between them the buckets cover every file,
 * including ones uploaded before this list existed and ones with no subject at all.
 */
export function buildSubjectFilterOptions(
  configuredSubjects: string[],
  subjectsInUse: (string | null | undefined)[],
): { value: string; isOther: boolean }[] {
  const present = subjectsInUse.map((subject) => (subject ?? "").trim());

  const options = configuredSubjects
    .filter((subject) => present.includes(subject))
    .map((subject) => ({ value: subject, isOther: false }));

  if (present.some((subject) => isOtherSubject(subject, configuredSubjects))) {
    options.push({ value: OTHER_SUBJECT_VALUE, isOther: true });
  }

  return options;
}
